
(() => {
    if (window.lucidiusVolumeRuntime) return;
    window.lucidiusVolumeRuntime = true;
    const pending = new Map();
    const ttl = 10 * 60 * 1000;
    const amount = value => {
      const number = String(value).replace(/[^\d.,]/g, '');
      if (!/^\d+(?:[.,]\d{1,2})?$/.test(number)) return null;
      return Math.round(Number(number.replace(',', '.')) * 100);
    };
    const fromDiscount = discount => {
      if (Number(discount.operand_type) !== 1) return [];
      if (!['BOTH', 'ONE_TIME'].includes(discount.purchaseType)) return [];
      return (discount.tiers || []).map(tier => ({
        threshold: Number(tier.criteria),
        type: Number(discount.discount_type),
        amount: amount(tier.discount)
      })).filter(tier => Number.isSafeInteger(tier.threshold) && tier.threshold > 1 && [1, 2, 3, 4].includes(tier.type) && tier.amount > 0);
    };
    const embedded = variantId => {
      const products = window.$Discounty?.products;
      if (!Array.isArray(products)) return null;
      for (const product of products) {
        const variant = product.variants?.find(variant => String(variant.id) === variantId);
        if (variant) {
          const discount = variant.discounts?.[0] || {};
          if (discount.meta_variant_id != null && String(discount.meta_variant_id) !== variantId) return [];
          return fromDiscount(discount);
        }
      }
      return null;
    };
    const readPage = (html, variantId) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = Array.from(doc.querySelectorAll('script')).find(script => script.textContent.includes('var $Discounty ='));
      if (!script) return [];
      const text = script.textContent;
      const marker = new RegExp('\\bid:\\s*' + variantId + '\\b').exec(text);
      if (!marker) return [];
      const block = text.slice(marker.index).split('}],')[0];
      const variantTarget = /meta_variant_id:\s*"(\d+)"/.exec(block);
      if (variantTarget && variantTarget[1] !== variantId) return [];
      const operand = /operand_type:\s*parseInt\("(\d+)"\)/.exec(block);
      const type = /discount_type:\s*parseInt\("(\d+)"\)/.exec(block);
      const purchase = /purchaseType:\s*"([A-Z_]+)"/.exec(block);
      if (Number(operand?.[1]) !== 1 || !['BOTH', 'ONE_TIME'].includes(purchase?.[1])) return [];
      const tiers = Array.from(block.matchAll(/criteria:\s*"(\d+)"\s*,\s*discount:\s*discountyExtractTextFromHtml\(`([^`]*)`\)/g));
      return tiers.map(match => ({ threshold: Number(match[1]), type: Number(type?.[1]), amount: amount(match[2]) })).filter(tier => tier.threshold > 1 && [1, 2, 3, 4].includes(tier.type) && tier.amount > 0);
    };
    const load = (url, variantId) => {
      const live = embedded(variantId);
      if (live !== null) return Promise.resolve(live);
      const key = 'lucidius-volume-v1:' + location.host + ':' + url + ':' + variantId;
      if (pending.has(key)) return pending.get(key);
      try {
        const cached = JSON.parse(sessionStorage.getItem(key));
        if (cached && Date.now() - cached.time < ttl && Array.isArray(cached.tiers)) return Promise.resolve(cached.tiers);
      } catch {}
      const request = fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(5000) })
        .then(response => response.ok ? response.text() : '')
        .then(html => readPage(html, variantId))
        .then(tiers => {
          try { sessionStorage.setItem(key, JSON.stringify({time: Date.now(), tiers})); } catch {}
          return tiers;
        }).catch(() => []).finally(() => pending.delete(key));
      pending.set(key, request);
      return request;
    };
    class VolumeNudge extends HTMLElement {
      disconnectedCallback() {
        this.openObserver?.disconnect();
        this.openResolve?.();
      }
      async connectedCallback() {
        const quantity = Number(this.dataset.quantity);
        const ratio = Number(this.dataset.ratio);
        const original = Number(this.dataset.originalPrice);
        let threshold = Number(this.dataset.threshold);
        const tierPrice = Number(this.dataset.unitPrice);
        let saving = tierPrice > 0 && tierPrice < original ? original - tierPrice : 0;
        if (!threshold) {
          const drawer = this.closest('cart-drawer');
          if (drawer && !drawer.classList.contains('active')) {
            await new Promise(resolve => {
              this.openResolve = resolve;
              this.openObserver = new MutationObserver(() => {
                if (drawer.classList.contains('active')) {
                  this.openObserver.disconnect();
                  resolve();
                }
              });
              this.openObserver.observe(drawer, {attributes: true, attributeFilter: ['class']});
            });
          }
          if (!this.isConnected) return;
          const url = new URL(this.dataset.productUrl, location.origin);
          if (url.origin !== location.origin || !/^\d+$/.test(this.dataset.variantId)) return;
          const tiers = await load(url.pathname, this.dataset.variantId);
          const next = tiers.filter(tier => tier.threshold > quantity).sort((a, b) => a.threshold - b.threshold)[0];
          if (!next) return;
          threshold = next.threshold;
          if (next.amount !== null) {
            saving = tierSaving(next, original);
          }
        }
        if (!this.isConnected || quantity < Math.ceil(ratio * threshold / 100) || quantity >= threshold) return;
        let copy = this.dataset.copy.replace('__quantity__', String(threshold - quantity));
        if (saving > 0 && saving <= original) {
          const number = new Intl.NumberFormat(this.dataset.locale, {minimumFractionDigits: saving % 100 === 0 ? 0 : 2, maximumFractionDigits: 2}).format(saving / 100);
          const money = this.dataset.money.replace(/[\d.,\s\u00a0\u202f]+(?=[^\d.,]*$)/, number + ' ');
          copy += this.dataset.savingCopy.replace('__saving__', money.trim());
        }
        this.querySelector('p').textContent = copy;
        this.hidden = false;
        this.croVolume = {kind:'nudge', product_id:this.dataset.productId, variant_id:this.dataset.variantId, remaining:threshold - quantity};
        this.dispatchEvent(new CustomEvent('lucidius:volume-state', {bubbles:true}));
      }
    }

    const tierSaving = (tier, original) => tier.type === 3 ? tier.amount : tier.type === 4 ? original - tier.amount : 0;
    const formatMoney = (element, cents, compact = true) => {
      const number = new Intl.NumberFormat(element.dataset.locale, {minimumFractionDigits: compact && cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2}).format(cents / 100);
      return element.dataset.money.replace(/[\d.,\s\u00a0\u202f]+(?=[^\d.,]*$)/, number + ' ').trim();
    };
    class ProductVolume extends HTMLElement {
      connectedCallback() {
        this.product = this.closest('product-info');
        if (!this.product) return;
        this.variantId = this.dataset.variantId;
        this.original = Number(this.dataset.originalPrice);
        this.cartItems = null;
        this.cartRequest = 0;
        this.refreshCart = async () => {
          const request = ++this.cartRequest;
          try {
            const root = window.Shopify?.routes?.root || '/';
            const response = await fetch(root + 'cart.js', {credentials:'same-origin', cache:'no-store'});
            if (!response.ok) return;
            const cart = await response.json();
            if (request !== this.cartRequest || !this.isConnected || !Array.isArray(cart.items)) return;
            this.cartItems = cart.items;
            this.schedule();
          } catch {}
        };
        this.schedule = () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.update());
        };
        this.onQuantity = event => {
          if (event.target.matches('input[name="quantity"], input[name="selling_plan"]')) this.schedule();
        };
        this.product.addEventListener('input', this.onQuantity);
        this.product.addEventListener('change', this.onQuantity);
        this.unsubscribe = typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (event.data?.sectionId !== this.dataset.section) return;
          this.variantId = String(event.data.variant.id);
          this.original = event.data.variant.price;
          this.nativePrice = null;
          this.schedule();
          this.refreshCart();
        }) : null;
        this.unsubscribeQuantity = typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.quantityUpdate, this.schedule) : null;
        this.unsubscribeCart = typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.cartUpdate, this.refreshCart) : null;
        document.addEventListener('cart:update', this.refreshCart);
        this.onWindowCart = event => { if (event.target === window) this.refreshCart(); };
        window.addEventListener('cart:update', this.onWindowCart);
        this.refreshCart();
        this.timer = setInterval(this.schedule, 250);
        this.timeout = setTimeout(() => clearInterval(this.timer), 10000);
        this.update();
      }
      update() {
        const price = this.product.querySelector('[id^="price-"]');
        if (!price) return;
        const tiers = (embedded(String(this.variantId)) || []).filter(tier => {
          const saving = tierSaving(tier, this.original);
          return saving > 0 && saving < this.original;
        }).sort((a,b) => a.threshold - b.threshold);
        const subscription = this.product.querySelector('input[name="selling_plan"]')?.value;
        const input = this.product.querySelector('input[name="quantity"]');
        const inputQuantity = Number(input?.value);
        const cartQuantity = this.cartItems === null ? Number(input?.dataset.cartQuantity || 0) : this.cartItems
          .filter(item => String(item.variant_id) === String(this.variantId))
          .reduce((total, item) => total + item.quantity, 0);
        const quantity = cartQuantity + inputQuantity;
        const eligible = tiers.length > 0 && !subscription;
        this.hidden = !eligible;
        const active = eligible && Number.isSafeInteger(inputQuantity) && inputQuantity > 0 ? tiers.filter(tier => tier.threshold <= quantity).at(-1) : null;
        let copy = '';
        let unitCopy = '';
        let remaining = 0;
        if (active) {
          const saving = tierSaving(active, this.original);
          copy = this.dataset.reductionCopy.replace('__saving__', formatMoney(this, quantity * saving));
          const unitMoney = formatMoney(this, this.original - saving, false);
          unitCopy = Number(this.dataset.unitCount) > 0
            ? unitMoney + this.dataset.discountUnit
            : this.dataset.unitCopy.replace('__price__', unitMoney);
          if (!this.nativePrice) this.nativePrice = price.innerHTML;
          let visual = price.querySelector('.lucidius-product-volume-price');
          if (!visual) {
            price.innerHTML = '';
            visual = document.createElement('div');
            visual.className = 'price price--large lucidius-product-volume-price';
            const old = document.createElement('s');
            old.setAttribute('aria-label', this.dataset.regularLabel);
            visual.append(old);
            const current = document.createElement('strong');
            current.className = 'price-item';
            current.setAttribute('aria-label', this.dataset.saleLabel);
            if (this.dataset.unitSuffix) {
              const amountLabel = document.createElement('span');
              amountLabel.className = 'lucidius-price-amount';
              const unitLabel = document.createElement('span');
              unitLabel.className = 'lucidius-price-unit';
              unitLabel.textContent = this.dataset.unitSuffix;
              current.append(amountLabel, unitLabel);
            }
            visual.append(current);
            price.append(visual);
          }
          const old = formatMoney(this, this.original, false);
          if (visual.firstChild.textContent !== old) visual.firstChild.textContent = old;
          if (this.dataset.unitSuffix) {
            const amountLabel = visual.lastChild.querySelector('.lucidius-price-amount');
            const displayMoney = formatMoney(this, this.original - saving, false);
            if (amountLabel.textContent !== displayMoney) amountLabel.textContent = displayMoney;
          } else if (visual.lastChild.textContent !== unitCopy) visual.lastChild.textContent = unitCopy;
        } else {
          if (this.nativePrice) { price.innerHTML = this.nativePrice; this.nativePrice = null; }
          const next = eligible ? tiers.find(tier => tier.threshold > quantity) : null;
          if (next && Number.isSafeInteger(inputQuantity) && inputQuantity > 0 && quantity >= Math.ceil(Number(this.dataset.ratio) * next.threshold / 100)) {
            remaining = next.threshold - quantity;
            copy = this.dataset.copy.replace('__quantity__', String(remaining)) + this.dataset.savingCopy.replace('__saving__', formatMoney(this, tierSaving(next, this.original)));
          }
        }
        const pill = this.querySelector('p');
        if (pill.textContent !== copy) pill.textContent = copy;
        const volumeState = {kind:active ? 'tier' : remaining ? 'nudge' : 'none', product_id:this.product.dataset.productId, variant_id:this.variantId};
        if (active) { volumeState.qty = quantity; volumeState.saving = tierSaving(active, this.original) * quantity / 100; }
        if (remaining) volumeState.remaining = remaining;
        if (JSON.stringify(this.croVolume) !== JSON.stringify(volumeState)) {
          this.croVolume = volumeState;
          this.dispatchEvent(new CustomEvent('lucidius:volume-state', {bubbles:true}));
        }
        if (eligible) this.product.dataset.volumeDisplayPriceCents = String(active ? this.original - tierSaving(active, this.original) : this.original);
        else delete this.product.dataset.volumeDisplayPriceCents;
        const previous = this.product.dataset.volumeUnitPrice || '';
        if (unitCopy) this.product.dataset.volumeUnitPrice = unitCopy;
        else delete this.product.dataset.volumeUnitPrice;
        if (previous !== unitCopy) this.product.dispatchEvent(new CustomEvent('lucidius:volume-price', {bubbles:true}));
      }
      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        clearInterval(this.timer);
        clearTimeout(this.timeout);
        this.unsubscribe?.();
        this.unsubscribeCart?.();
        this.unsubscribeQuantity?.();
        ++this.cartRequest;
        document.removeEventListener('cart:update', this.refreshCart);
        window.removeEventListener('cart:update', this.onWindowCart);
        this.product?.removeEventListener('input', this.onQuantity);
        this.product?.removeEventListener('change', this.onQuantity);
      }
    }
    if (!customElements.get('lucidius-product-volume')) customElements.define('lucidius-product-volume', ProductVolume);
    if (!customElements.get('lucidius-volume-nudge')) customElements.define('lucidius-volume-nudge', VolumeNudge);
  })();
